import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Location} from '@angular/common';
import {MatDialog} from '@angular/material/dialog';
import {NavigationStart, Router} from '@angular/router';
import {defer, EMPTY, Observable, of, Subject, Subscription} from 'rxjs';
import {catchError, concatMap, map, tap} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {InterfaceProviderService} from '../basic-entity-back/services/interface-provider.service';
import {ErrorDisplayService} from '../basic-entity-front/services/error-display.service';
import {BaseEntity} from '../model/base-entity.model';
import {Token} from '../api/session/token';
import {CABECERA_SESION_EDICION, SesionEdicionService} from './sesion-edicion.service';

/**
 * Bloqueo de edición visto desde esta pestaña. La API decide quién tiene cada registro (por
 * sesión, con caducidad a los 3 minutos); aquí solo se pide, se mantiene vivo con un latido y se
 * suelta. Regla que evita el fallo histórico: esta pestaña SOLO libera lo que ella adquirió, así
 * que entrar y salir de un editor nunca suelta el bloqueo de otro equipo.
 */
@Injectable({providedIn: 'root'})
export class BloqueadorService implements OnDestroy {
    static readonly LATIDO_MS = 60000;

    /** IRIs que tiene bloqueados esta pestaña */
    private _mios = new Set<string>();
    /**
     * Adquisiciones pedidas y aún sin respuesta, UNA ENTRADA POR INTENTO (no por registro): del mismo
     * IRI puede haber varias a la vez (doble clic en una fila, "seleccionar todo" pulsado dos veces),
     * y cada respuesta tiene que saber si alguien sigue esperando ESE intento, no ese registro.
     */
    private _intentos = new Map<number, { iri: string, cancelado: boolean }>();
    private _ultimoIntento = 0;
    private _latido: any = null;
    /** Las peticiones salen de una en una: si el editor A libera y el B adquiere el mismo registro, en ese orden */
    private _cola = new Subject<() => Observable<any>>();
    private _navegacion: Subscription;
    private readonly _alDescargar = () => this._liberarTodoAlCerrar();

    constructor(private _http: HttpClient, private _intf: InterfaceProviderService, private _sesion: SesionEdicionService,
                private _errores: ErrorDisplayService, private _dialogos: MatDialog, private _location: Location,
                private _zone: NgZone, private _router: Router) {
        this._cola.pipe(concatMap(tarea => tarea())).subscribe();
        window.addEventListener('pagehide', this._alDescargar);
        this._navegacion = this._router.events.subscribe(evento => {
            // Solo el comienzo de la navegación sirve: para entonces el componente de destino aún no
            // existe, así que lo que hay apuntado es exactamente lo de la pantalla que se abandona.
            if (evento instanceof NavigationStart && BloqueadorService._ruta(this._router.url) !== BloqueadorService._ruta(evento.url)) {
                this._alCambiarDePantalla();
            }
        });
    }

    ngOnDestroy(): void {
        window.removeEventListener('pagehide', this._alDescargar);
        this._navegacion?.unsubscribe();
        this._pararLatido();
    }

    bloquear<T extends BaseEntity>(m: T): Observable<T | null> {
        const iri = this._iri(m);
        // `defer` para apuntar el intento al suscribirse, que es el mismo instante en que la petición
        // entra en la cola: desde ahí un desbloquear() o una navegación tienen que poder cancelarlo
        // aunque la petición todavía no haya salido. Apuntar y encolar en el mismo turno es lo que
        // permite razonar sobre el orden (ver _hayIntentoVivo).
        return defer(() => {
            const idIntento = ++this._ultimoIntento;
            this._intentos.set(idIntento, {iri, cancelado: false});
            return this._encolar<T | null>(() => this._http.post<{ bloqueable?: boolean }>(environment.apiUrl + '/bloqueos/adquirir', {recurso: iri}, {headers: this._cabeceras()}).pipe(
                concatMap(respuesta => {
                    const cancelado = this._cerrarIntento(idIntento);
                    if (cancelado) {
                        // Se pidió soltarlo mientras esta adquisición viajaba (la pantalla ya se fue). Solo
                        // se suelta si NADIE quiere ya el registro: el bloqueo del servidor es de la sesión,
                        // no de este intento, así que liberarlo con otro intento vivo (o ya concedido) del
                        // mismo IRI le quitaría el registro a quien sí lo está esperando.
                        if (respuesta?.bloqueable !== false && !this._mios.has(iri) && !this._hayIntentoVivo(iri)) {
                            // Por la cola, y sin esperarla: encolar y esperar desde dentro de una tarea de la
                            // cola sería un interbloqueo.
                            this._liberarIris([iri]).subscribe();
                        }
                        // Quien lo pidió ya no lo quiere: este observable no emite nada, solo termina.
                        return EMPTY as Observable<T | null>;
                    }
                    // Hay modelos del front que son BaseEntity pero cuyo recurso en la API no admite bloqueo
                    // (p.ej. una tarifa): se abre igual, pero esta pestaña no tiene nada que mantener vivo ni
                    // que soltar, así que no se apunta ni arranca el latido (si no, el próximo latido lo daría
                    // por perdido y saltaría el aviso sin que nadie lo hubiera tenido nunca).
                    if (respuesta?.bloqueable !== false) {
                        this._mios.add(iri);
                        this._arrancarLatido();
                    }
                    return of(m);
                }),
                catchError(error => {
                    if (this._cerrarIntento(idIntento)) {
                        // Cancelado y encima ha fallado: la pantalla que lo pidió ya no está, así que el aviso
                        // y el cierre de diálogos caerían sobre la de destino. Silencio.
                        return EMPTY as Observable<T | null>;
                    }
                    this._errores.displayError(error);
                    this._dialogos.closeAll();
                    return of(null);
                })
            ));
        });
    }

    /** Da un intento por terminado y dice si alguien lo había cancelado mientras viajaba */
    private _cerrarIntento(idIntento: number): boolean {
        const intento = this._intentos.get(idIntento);
        this._intentos.delete(idIntento);
        return !!intento?.cancelado;
    }

    /** Si queda algún intento de ese registro que alguien siga esperando */
    private _hayIntentoVivo(iri: string): boolean {
        let vivo = false;
        this._intentos.forEach(intento => {
            if (intento.iri === iri && !intento.cancelado) {
                vivo = true;
            }
        });
        return vivo;
    }

    /** Marca como cancelados los intentos en vuelo que cumplan el filtro */
    private _cancelarIntentos(filtro: (intento: { iri: string, cancelado: boolean }) => boolean) {
        this._intentos.forEach(intento => {
            if (filtro(intento)) {
                intento.cancelado = true;
            }
        });
    }

    /** Para pantallas con URL propia: si no se concede, aviso y vuelta atrás. */
    adquirirOVolver<T extends BaseEntity>(m: T): Observable<boolean> {
        return this.bloquear(m).pipe(map(r => {
            if (!r) {
                this._location.back();
            }
            return !!r;
        }));
    }

    desbloquear<T extends BaseEntity>(m: T): Observable<T> {
        const iri = m ? this._iri(m) : null;
        if (iri) {
            // Las adquisiciones de ese registro que aún viajen se cancelan: si no, al llegar volverían
            // a apuntarlo justo después de haberlo soltado
            this._cancelarIntentos(intento => intento.iri === iri);
        }
        if (!iri || !this._mios.has(iri)) {
            return of(m);
        }
        // Se quita ya: si el latido salta mientras la liberación viaja, no debe renovarlo
        this._mios.delete(iri);
        this._pararLatidoSiNoQuedaNada();
        return this._liberarIris([iri]).pipe(map(() => m));
    }

    /**
     * Desbloqueo forzado: salida de emergencia de administración (la API exige ROLE_EMPLEADO).
     * Suelta el bloqueo SEA DE QUIEN SEA; quien lo tenía abierto se entera en su siguiente latido
     * (le llega en `perdidos` y recibe 423 si intenta guardar). Si el registro forzado resulta ser
     * de esta misma pestaña (raro: forzar lo propio), se quita también de lo suyo para no seguir
     * latiendo por algo que la API ya ha soltado.
     */
    forzar(m: BaseEntity): Observable<boolean> {
        const iri = this._iri(m);
        return this._encolar<boolean>(() => this._http.post<{ liberado?: boolean }>(environment.apiUrl + '/bloqueos/forzar', {recurso: iri}, {headers: this._cabeceras()}).pipe(
            map(respuesta => {
                if (this._mios.has(iri)) {
                    this._mios.delete(iri);
                    this._pararLatidoSiNoQuedaNada();
                }
                return respuesta?.liberado === true;
            }),
            catchError(error => {
                this._errores.displayError(error);
                return of(false);
            })
        ));
    }

    bloqueado(model: BaseEntity): boolean {
        return !!model.bloqueadoPor && model.bloqueadoPor !== '';
    }

    /**
     * Manda una liberación por la cola. No toca el registro de esta pestaña: eso ya lo ha hecho
     * quien llama, para que un latido que se cruce no renueve lo que se está soltando.
     */
    private _liberarIris(iris: string[]): Observable<any> {
        return this._encolar(() => this._http.post(environment.apiUrl + '/bloqueos/liberar', {recursos: iris}, {headers: this._cabeceras()}).pipe(
            catchError(() => of(null)) // si falla, caduca solo
        ));
    }

    /**
     * Red de seguridad: al cambiar de pantalla, esta pestaña suelta todo lo que tuviera ANTES de la
     * navegación (lo concedido y lo que estuviera en vuelo, que se cancela). La foto se hace en
     * NavigationStart, antes de que Angular cree el componente de destino, así que lo que ese
     * adquiera en su init no entra en ella; y como todo va por la cola, la liberación de lo viejo
     * sale antes que la adquisición de lo nuevo aunque sea el mismo registro.
     */
    private _alCambiarDePantalla() {
        this._cancelarIntentos(() => true);
        const aLiberar = Array.from(this._mios);
        this._mios.clear();
        this._pararLatidoSiNoQuedaNada();
        if (aLiberar.length > 0) {
            this._liberarIris(aLiberar).subscribe();
        }
    }

    /** La ruta sin query ni fragmento: cambiar los filtros o la página de un listado no es cambiar de pantalla. */
    private static _ruta(url: string): string {
        return (url || '').split('#')[0].split('?')[0];
    }

    private _encolar<R>(peticion: () => Observable<R>): Observable<R> {
        return new Observable<R>(sub => {
            this._cola.next(() => peticion().pipe(tap({
                next: v => { sub.next(v); sub.complete(); },
                // Una tarea puede terminar sin emitir (adquisición cancelada): el suscriptor tiene que
                // enterarse igual, o se queda esperando para siempre
                complete: () => sub.complete(),
                error: e => sub.error(e)
            }), catchError(() => of(null))));
        });
    }

    /** El IRI que espera la API: una ruta relativa, sin `environment.apiUrl`, aunque endpointFor devolviera la URL completa. */
    private _iri(m: BaseEntity): string {
        const url = this._intf.interfaceForModel(m.modelType).endpointFor(m);
        return url.startsWith(environment.apiUrl) ? url.substring(environment.apiUrl.length) : url;
    }

    /**
     * Ningún interceptor añade el JWT a estas peticiones (solo `ApiService` lo hace, en las suyas
     * propias): hay que montarlo aquí. Se lee en cada llamada, nunca se cachea, porque el token
     * puede renovarse mientras la pestaña sigue abierta.
     */
    private _cabeceras(): { [k: string]: string } {
        const token = Token.fromLocalStorage();
        return token.isValid() ? {Authorization: `Bearer ${token.token}`} : {};
    }

    private _arrancarLatido() {
        if (this._latido !== null) {
            return;
        }
        // Fuera de la zona de Angular: un temporizador eterno no debe disparar detección de cambios
        this._zone.runOutsideAngular(() => {
            this._latido = setInterval(() => this._zone.run(() => this._renovar()), BloqueadorService.LATIDO_MS);
        });
    }

    private _pararLatido() {
        clearInterval(this._latido);
        this._latido = null;
    }

    private _pararLatidoSiNoQuedaNada() {
        if (this._mios.size === 0) {
            this._pararLatido();
        }
    }

    private _renovar() {
        if (this._mios.size === 0) {
            return;
        }
        this._http.post<{ renovados: string[], perdidos: string[] }>(environment.apiUrl + '/bloqueos/renovar', {recursos: Array.from(this._mios)}, {headers: this._cabeceras()})
            .subscribe(r => {
                // Solo es una pérdida si el registro seguía siendo nuestro: un IRI que esta pestaña soltó
                // mientras el latido viajaba vuelve en `perdidos` sin que nadie haya perdido nada.
                const perdidos = (r?.perdidos || []).filter(iri => this._mios.has(iri));
                perdidos.forEach(iri => this._mios.delete(iri));
                this._pararLatidoSiNoQuedaNada();
                if (perdidos.length > 0) {
                    this._errores.displayRaw('Has perdido el bloqueo de este registro. Recarga la página antes de seguir editando.');
                }
            }, () => {
                // Sin red o sesión caducada: no se hace nada; si dura más de 3 minutos, el bloqueo caduca
            });
    }

    /** Al cerrar la pestaña: fetch con keepalive, porque sendBeacon no admite la cabecera Authorization. */
    private _liberarTodoAlCerrar() {
        if (this._mios.size === 0) {
            return;
        }
        // A un fetch no le aplica ningún interceptor: hay que añadir a mano también la cabecera de sesión
        fetch(environment.apiUrl + '/bloqueos/liberar', {
            method: 'POST',
            keepalive: true,
            headers: {
                'Content-Type': 'application/json',
                [CABECERA_SESION_EDICION]: this._sesion.id,
                ...this._cabeceras()
            },
            body: JSON.stringify({recursos: Array.from(this._mios)})
        }).catch(() => undefined);
    }
}
